// Nav-bar re-tap → back to the top (polish pass, deviation 22). Screens own
// their scroll containers (the document never scrolls, so iOS's tap on the
// status bar cannot do this), so the shell looks for the pane's main one.

function scrollsVertically(element: HTMLElement): boolean {
  if (element.scrollHeight <= element.clientHeight + 1) return false;
  const overflow = getComputedStyle(element).overflowY;
  return overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay';
}

/** Area of `element` visible inside `bounds` (0 when off screen, e.g. a section paged away). */
function visibleArea(element: HTMLElement, bounds: DOMRect): number {
  const rect = element.getBoundingClientRect();
  const width = Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left);
  const height = Math.min(rect.bottom, bounds.bottom) - Math.max(rect.top, bounds.top);
  return width > 0 && height > 0 ? width * height : 0;
}

/**
 * The pane's main vertical scroller: of the elements that can scroll
 * vertically and are on screen, the one covering the most of the pane (a
 * screen's list rather than a chip row or a nested panel). Inert subtrees
 * (covered sections, leaving layers) are skipped.
 */
export function findMainScroller(pane: HTMLElement): HTMLElement | null {
  const bounds = pane.getBoundingClientRect();
  let best: HTMLElement | null = null;
  let bestArea = 0;
  const walker = document.createTreeWalker(pane, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => ((node as Element).hasAttribute('inert') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const element = node as HTMLElement;
    if (!scrollsVertically(element)) continue;
    const area = visibleArea(element, bounds);
    if (area > bestArea) {
      best = element;
      bestArea = area;
    }
  }
  return best;
}

/** Scrolls the pane's main scroller back to the top: smoothly, or at once with reduced motion. */
export function scrollPaneToTop(pane: HTMLElement, reducedMotion: boolean): void {
  const scroller = findMainScroller(pane);
  if (!scroller || scroller.scrollTop <= 0) return;
  scroller.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
}
